import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import aakritiLogo from "@/assets/aakriti-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="hidden md:flex relative bg-primary text-white flex-col justify-between p-12 noise-overlay overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-accent/10 blur-3xl" />
        <Link to="/" className="flex items-center gap-2.5 relative z-10" data-testid="login-brand">
          <div className="h-10 rounded-md bg-white flex items-center justify-center px-1.5"><img src={aakritiLogo} alt="Aakriti" className="h-7 w-auto object-contain" /></div>
          <div className="font-display font-bold text-lg">Aakriti Associates</div>
        </Link>
        <div className="relative z-10">
          <h1 className="font-display font-black text-4xl lg:text-5xl leading-tight">Grow your valuation business.</h1>
          <p className="text-white/70 mt-4 max-w-md">Track every bank branch, follow-up, and valuation job in one place — and let your team deliver on time, every time.</p>
        </div>
        <div className="relative z-10 text-xs tracking-[0.2em] uppercase text-white/40">Raipur · Chhattisgarh</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-8 transition-colors" data-testid="back-home"><ArrowLeft size={16} /> Back to site</Link>
          <h2 className="font-display font-bold text-3xl text-primary">Team Login</h2>
          <p className="text-muted-foreground mt-1 text-sm">Sign in to your workspace</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" name="email" autoComplete="username" required data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input type="password" name="password" autoComplete="current-password" required data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full bg-primary hover:bg-[#113A64]">{loading ? "Signing in…" : "Sign In"}</Button>
          </form>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-sm">
            <Link to="/forgot-password" className="text-muted-foreground hover:text-accent transition-colors" data-testid="link-forgot-password">Forgot password?</Link>
            <Link to="/forgot-userid" className="text-muted-foreground hover:text-accent transition-colors" data-testid="link-forgot-userid">Forgot login ID?</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
